import {
  StoplightIndicator,
  StoplightLabel,
} from "@/components/stoplight-indicator";
import { formatNumber, showTimeDiffPartial } from "@/lib/utils";
import { DSContractState } from "@myth-finance/dualstake-ts-sdk";
import { cx } from "class-variance-authority";
import { ChevronDown, ChevronRight } from "lucide-react";
import {
  ApplicationEscrowLink,
  ApplicationLink,
  AssetLink,
} from "@/components/links";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from "@/components/ui/collapsible";
import { DSContractListingExtd } from "@/lib/types";

export function InfoBareCard({
  listing,
  className,
  cState,
}: {
  className?: string;
  cState: DSContractState;
  listing: DSContractListingExtd;
}) {
  return (
    <Collapsible
      className={cx(
        `group flex-col text-muted-foreground/90 flex min-w-[300px] w-full max-w-[350px] p-0 mt-0 gap-1 items-stretch data-[state=open]:mb-4`,
        className
      )}
    >
      <div>
        <CollapsibleTrigger
          asChild
          className="flex items-center gap-0 text-muted-foreground group-data-[state=open]:text-foreground"
        >
          <div className="cursor-pointer">
            <Button variant="ghost" size="sm">
              <ChevronDown className="h-4 w-4 group-data-[state=closed]:hidden" />
              <ChevronRight className="h-4 w-4 group-data-[state=open]:hidden" />
              <span className="sr-only">Toggle</span>
            </Button>
            <span>Asset & Contract Info</span>
          </div>
        </CollapsibleTrigger>
      </div>
      <CollapsibleContent className="space-y-2 pt-2 px-4">
        <div className="flex flex-col">
          <div className="flex justify-between items-center">
            <div>Rewards Status</div>
            <div className="flex gap-1 items-center">
              <StoplightIndicator
                online={cState.isOnline}
                eligible={cState.incentiveEligible}
                className="pr-0 ml-1"
                name={cState.lstName}
                value={cState.staked}
                iconSize={18}
                sunset={listing.sunset}
              >
                <StoplightLabel
                  online={cState.isOnline}
                  eligible={cState.incentiveEligible}
                  value={cState.staked}
                  sunset={listing.sunset}
                />
              </StoplightIndicator>
            </div>
          </div>
          <div className="flex justify-between items-center">
            <div>Consensus status</div>
            <div>
              {cState.isOnline ? (
                "Online"
              ) : (
                <span className="text-destructive/80">Offline</span>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col">
          <div className="flex justify-between items-center">
            <div>ASA ID {cState.asaUnitName}</div>
            <AssetLink id={cState.asaId} />
          </div>
          <div className="flex justify-between items-center">
            <div>ASA ID {cState.lstName} </div>
            <AssetLink id={cState.lstId} />
          </div>
        </div>

        <div className="flex flex-col">
          <div className="flex justify-between items-center">
            <div>App ID {cState.lstName}</div>
            <ApplicationLink id={cState.appId} />
          </div>

          <div className="flex justify-between items-center">
            <div>Escrow {cState.lstName}</div>
            <ApplicationEscrowLink short={true} id={cState.appId} />
          </div>
        </div>

        <div className="flex flex-col">
          {cState.maxBalance < BigInt(65_000_000 * 1e6) ? (
            <div className="flex justify-between items-center text-destructive/80">
              <div>Staking limit</div>
              <div>{formatNumber(cState.maxBalance)} ALGO</div>
            </div>
          ) : null}

          <div className="flex justify-between items-center">
            <div>Staked ALGO</div>
            {formatNumber(cState.staked)}
          </div>

          <div className="flex justify-between items-center">
            <div>Circulating {cState.lstName}</div>
            {formatNumber(cState.staked - cState.protestSum)}
          </div>

          {cState.asaBalance > 0n ? (
            <div className="flex justify-between items-center">
              <div>Paired {cState.asaUnitName}</div>
              {formatNumber(cState.asaBalance, cState.asaDecimals, 2)}
            </div>
          ) : null}

          {cState.protestSum > 0n ? (
            <div className="flex justify-between items-center">
              <div>Protesting {cState.lstName}</div>
              {formatNumber(cState.protestSum)}
            </div>
          ) : null}
        </div>

        <div className="flex flex-col">
          <div className="flex justify-between items-center">
            <div>Fee update time lock</div>
            {showTimeDiffPartial(Number(1000n * cState.feeUpdatePeriod))}
          </div>
          <div className="flex justify-between items-center">
            <div>Code upgrade time lock</div>
            {showTimeDiffPartial(Number(1000n * cState.upgradePeriod))}
          </div>
        </div>

        {/* <div className="flex flex-col">
          <div className="flex justify-between items-center">
            <div>Smart Contract code</div>
            TODO deployed hash in react query
            <Link
              to={`https://github.com/MythFinance/dualSTAKE/tree/contract-${cState.contractUpgrade?.contractPageHashes[0]}`}
            >
              Github
            </Link>
          </div>
        </div> */}
      </CollapsibleContent>
    </Collapsible>
  );
}
